import Vue from 'vue';
import Router from 'vue-router';
import Cove from '@ctrack/cove';
import store from '@/store';
import AppLogin from '@view/AppLogin';
import AppHome from '@view/AppHome';
import PACONST from '@/support/constants';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: Cove.getConfig('appRoot'),
  scrollBehavior (to, from, savedPosition) {
    let position = { x: 0, y: 0 };
    if (savedPosition)
    {
      position = savedPosition;
    }

    // since we auto-scroll on the search page don't snap back to the top -- keep the current position
    if (to && to.name === 'search' && from && from.name === to.name)
    {
      return {};
    }
    else
    {
      return position;
    }

  },
  routes: [
    {
      path: '*',
      name: 'error404',
      component: () => {return import('@view/Error404');},
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/',
      alias: '/home',
      name: 'home',
      component: AppHome
    },
    {
      path: '/case/:caseID',
      name: 'caseView',
      component: () => {return import('@view/CaseView');},
      props: (route) => {return {
        caseID: route.params.caseID
      };}
    },
    {
      path: '/case/summary/:caseID',
      name: 'caseViewSummary',
      component: () => {return import('@view/CaseViewSummary');},
      props: (route) => {return {
        caseID: route.params.caseID
      };},
      meta: {
        componentID: PACONST.COMPONENT_CASE_VIEW_SUMMARY
      }
    },
    {
      path: '/documents/:documentID/download',
      name: 'documentDownload',
      component: () => {return import('@view/DocumentDownload');},
      props: (route) => {return {
        documentID: route.params.documentID
      };},
      meta: { // TODO: remove once crosstab authentication is working
        requiresAuth: false
      }
    },
    {
      path: '/login',
      name: 'login',
      component: AppLogin,
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/search/:searchType',
      name: 'search',
      component: () => {return import('@view/AppSearch');},
      props: (route) => {return {
        searchType: route.params.searchType
      };}
    }
  ]
});

/**
 * Returns whether or not the specified route requires user authentication.
 *
 * @param {Object} route - a vue-router route object
 * @returns {boolean}
 */
function isRouteProtected (route) {
  let meta = route.meta;
  return (meta.requiresAuth === undefined || meta.requiresAuth === true);
}

/**
 * Returns whether or not the user object has the permissions to access the specified route
 *
 * @param {Object} route - a vue router route object
 * @returns {boolean}
 */
function hasRouteAccess (route) {
  let meta = route.meta;
  return (meta.componentID === undefined
    || store.getters['user/hasAccess'](meta.componentID, PACONST.PERMISSION_TYPES.READ));
}

/* order of these hooks is important - do not disturb */

router.beforeEach((to, from, next) => {
  if (to.matched.some((record) => { return isRouteProtected(record); } ))
  {
    if (!store.getters['user/isLoggedIn'])
    {
      window.localStorage.setItem('nextURL', to.fullPath);
      // this additional check is to better handle the progress bar cleanup during auth redirects
      if (from.name !== 'login')
      {
        next({ name: 'login' });
      }
      else
      {
        next(false);
      }
    }
    else if (!to.matched.every((record) => {return hasRouteAccess(record);}))
    {
      next({ name: 'error404', params: { '0': to.path } });
    }
    else
    {
      next();
    }
  }
  else
  {
    next();
  }
});

router.beforeEach((to, from, next) => {
  Cove.block();
  next();
});

router.afterEach(() => {
  Cove.unblock();
});

export default router;
